import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Upload } from 'lucide-react'
import { ArtworkStatus } from '@/types/entities'
import { useArtworks, useArtworkCount } from '../hooks/useArtworks'
import { useQuery } from '@tanstack/react-query'
import { salesByArtwork } from '@/features/sales/api/salesApi'
import { ArtworkTable } from '../components/ArtworkTable'
import { EmptyState } from '@/components/common/data-display/EmptyState'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { PAGINATION } from '@/config/constants'

export function ArtworkListPage() {
  // Published first. It is the state most artists are mostly in — Käthe has 39 published and 0
  // drafts — so opening on Draft showed an empty page to someone whose catalogue is entirely live.
  const [activeTab, setActiveTab] = useState<ArtworkStatus>(ArtworkStatus.PUBLISHED)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(PAGINATION.DEFAULT_PAGE_SIZE)

  const { data, isLoading } = useArtworks(activeTab, {
    start: (page - 1) * pageSize,
    limit: pageSize,
  })

  // Counts on the tabs, so the shape of the catalogue is readable without clicking through each
  // one. A tab showing "Pending 0" answers a question that an empty table only answers after a
  // round trip.
  // Only on Published. A Sold column beside a draft is three empty cells pretending to be data.
  const { data: sales } = useQuery({
    queryKey: ['sales', 'by-artwork'],
    queryFn: salesByArtwork,
    enabled: activeTab === ArtworkStatus.PUBLISHED,
    staleTime: 5 * 60 * 1000,
  })

  const draftCount = useArtworkCount(ArtworkStatus.DRAFT)
  const pendingCount = useArtworkCount(ArtworkStatus.PENDING)
  const publishedCount = useArtworkCount(ArtworkStatus.PUBLISHED)
  const counts: Record<string, number | undefined> = {
    [ArtworkStatus.DRAFT]: draftCount.data,
    [ArtworkStatus.PENDING]: pendingCount.data,
    [ArtworkStatus.PUBLISHED]: publishedCount.data,
  }

  return (
    <div className="space-y-8 p-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="font-serif text-4xl font-semibold tracking-tight">My Art</h1>
          <p className="text-muted-foreground text-base">Manage your artwork collection</p>
        </div>
        <Button asChild className="shadow-subtle">
          <Link to="/app/upload">
            <Upload className="mr-2 h-4 w-4" />
            Upload Art
          </Link>
        </Button>
      </div>

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ArtworkStatus)}>
          <TabsList className="w-full justify-start rounded-none border-b border-border/50 bg-transparent p-0">
            {[
              { value: ArtworkStatus.DRAFT, label: 'Draft' },
              { value: ArtworkStatus.PENDING, label: 'Pending' },
              { value: ArtworkStatus.PUBLISHED, label: 'Published' },
            ].map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary px-6"
              >
                {t.label}
                {counts[t.value] !== undefined && (
                  <span className="ml-2 text-xs tabular-nums text-muted-foreground">
                    {counts[t.value]}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeTab} className="p-6">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : !data || data.data.length === 0 ? (
              <EmptyState
                title="No artworks found"
                description={
                  activeTab === ArtworkStatus.DRAFT
                    ? 'Upload your first artwork to get started'
                    : `No ${activeTab.toLowerCase()} artworks yet`
                }
                action={
                  <Button asChild>
                    <Link to="/app/upload">
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Art
                    </Link>
                  </Button>
                }
              />
            ) : (
              <ArtworkTable
                artworks={data.data}
                sales={activeTab === ArtworkStatus.PUBLISHED ? sales : undefined}
                page={page}
                pageSize={pageSize}
                total={data.total}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            )}
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  )
}

